/**
 * Saves a merged-PDF download from the document merger modal.
 *
 * Uploads the merged blob to Firebase Storage, then upserts the
 * `submission_doc` row in Postgres. Fire-and-forget by design: errors are
 * logged, never thrown to the caller.
 *
 * @module documentMerger/saveMergedDocument
 */
import { PdfMergeDocument } from "@risalabs_frontend_org/oasis-ui-kit";
import { updateClinicalDocument } from "../../../api/postCall/updateClinicalDocument";
import { logError } from "../../../utils/customLogger";
import { uploadToFirebaseStorage } from "../../../utils/fileUpload";

/** Inputs for {@link saveMergedDocument}. */
interface SaveMergedDocumentParams {
  /** Merged PDF blob produced by the merge panel. */
  blob: Blob;
  /** Firebase Storage destination path for the merged PDF. */
  storagePath: string;
  /** PA order id the merged packet belongs to. */
  orderId: string;
  /** Organization id scoping the Postgres upsert. */
  orgId: string;
  /** Patient id persisted on the `submission_doc` row. */
  patientId: string;
  /** Per-source-document page ranges for the merged PDF. */
  documents: PdfMergeDocument[];
}

/**
 * Uploads the merged PDF and upserts its `submission_doc` record.
 *
 * Never rejects; failures are captured via {@link logError}.
 *
 * @param params - Blob plus order/org/patient identifiers for the write
 */
export const saveMergedDocument = ({
  blob,
  storagePath,
  orderId,
  orgId,
  patientId,
  documents,
}: SaveMergedDocumentParams): void => {
  void (async () => {
    try {
      const uploadedPath = await uploadToFirebaseStorage({
        file: blob,
        storagePath,
        fileName: "merged.pdf",
      });
      await updateClinicalDocument({
        order_id: orderId,
        org_id: orgId,
        data: {
          document_type: "submission_doc",
          patient_id: patientId,
          file_path: uploadedPath,
          document_name: storagePath.split("/").pop() ?? "",
          document_page_mapping: documents,
        },
      });
    } catch (error) {
      logError(error as Error, "Document merger failed to save merged PDF");
    }
  })();
};
